'use client';

import { CaretDown, CaretUp, Check } from '@phosphor-icons/react/ssr';
import { Select as SelectPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { menuStyles } from '../lib/menu-styles';
import { selectTrigger } from '../lib/select-styles';

/**
 * shadcn Select (Radix), restyled: the trigger is the Figma Select, the list
 * is the Figma Menu with Menu Items. Apps use `Select` (components/lib).
 */
const menu = menuStyles();

function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup(props: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue(props: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = 'md',
  leadingIcon: LeadingIcon,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'md' | 'lg';
  leadingIcon?: React.ElementType<{ className?: string }>;
}) {
  const styles = selectTrigger({ size });
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={styles.root({ className })}
      {...props}
    >
      {LeadingIcon && <LeadingIcon aria-hidden className={styles.leading()} />}
      <span className={styles.value()}>{children}</span>
      <SelectPrimitive.Icon asChild>
        <CaretDown aria-hidden className={styles.caret()} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        sideOffset={sideOffset}
        // Figma: attach 4px below, matching the trigger width.
        className={menu.content({
          className: cn(
            'max-h-(--radix-select-content-available-height) w-(--radix-select-trigger-width) origin-(--radix-select-content-transform-origin)',
            className,
          ),
        })}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex justify-center py-1">
          <CaretUp className="size-4" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex justify-center py-1">
          <CaretDown className="size-4" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={menu.header({ className })}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  leadingIcon: LeadingIcon,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & {
  leadingIcon?: React.ElementType<{ className?: string }>;
}) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={menu.item({ className })}
      {...props}
    >
      {LeadingIcon && <LeadingIcon aria-hidden />}
      <SelectPrimitive.ItemText>
        <span className={menu.label()}>{children}</span>
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className={menu.check()}>
        <Check aria-hidden />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={menu.divider({ className })}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
